import type { AxiosError, AxiosResponse } from 'axios';

import {
  TutorialExtraCategoryIdEnum,
  LanguageCodesEnum,
  TariffPlanCodeEnum,
  PaymentMethodEnum,
  TariffPlanStatusEnum,
  PostTypeEnum,
  PaymentMethodTypeEnum,
  PaymentMethodWalletEnum,
} from '@constants';
import { XOR } from '@types';

export type ApiErrorDataType = {
  errorCode: number;
  message: string;
  statusCode: number;
};

export type AxiosErrorResponseType = AxiosError<ApiErrorDataType> & {
  isNetworkError: boolean;
};

export type RefreshTokenRenewalControllerRequestType = (
  tokens: IRefreshTokenRequest,
) => Promise<AxiosResponse<ITokensResponse, IRefreshTokenRequest>>;

export type LogoutListenerCallbackType = (
  error?: AxiosError<ApiErrorDataType>,
) => void;

export interface ISuccessEmptyResponse {
  message: string;
}

export interface IPaginationResponse {
  count: number;
  nextStart: number;
  totalCount: number;
}

export interface IPaginationRequestParams {
  count?: number;
  start?: number;
}

export interface IQueryRequestParams {
  query: string;
}

export interface ICountResponse {
  count: number;
}

export interface IIdRequestParams {
  id: number;
}

export interface IIdOfStingTypeRequestParams {
  id: string;
}

export interface IIdResponse {
  id: number;
}

export interface IPathRequestParams {
  path: string;
}

export interface ISignInResponse extends ITokensResponse {
  isFirstLogin: boolean;
}

export interface ITokensResponse {
  token: string;
  refreshToken: string;
  refreshTokenExpiryTime: string;
}

export interface IRefreshTokenRequest {
  token: string;
  refreshToken: string;
}

export interface IUpdateLikeStatusParams {
  status: LikeStatusEnum;
}

export enum LikeStatusEnum {
  Like = 'like',
  Unlike = 'unlike',
}

export interface ISignInBodyRequest {
  email: string;
  password: string;
}

export interface ISignUpMobileInformationRequest {
  appleReceipt?: string;
  googleReceipt?: string;
}

export interface ISignUpStripeInformationRequest {
  paymentIntentId?: string;
  subscriptionId?: string;
}

export interface ISignUpBodyRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  countryCode: string;
  language: LanguageCodesEnum;
  tariffPlan: number;
  signUpDate: string;
  referrer?: string;
  referrerVideoId?: number;
  mobileInformation?: ISignUpMobileInformationRequest;
  /**
   * @deprecated
   */
  stripeInformation?: ISignUpStripeInformationRequest;
  origin?: string;
  couponId?: number;
  /**
   * @deprecated
   */
  sessionId?: number;
}

export interface ISignUpResponse {
  userId: number;
}

export interface ISignUpConfirmResponse {
  /**
   * We are using this prop to handle deduplication for conversion API
   * more: https://developers.facebook.com/docs/marketing-api/conversions-api/deduplicate-pixel-and-server-events/
   */
  conversionEventId: string;
}

export interface IVerifySignUpDataBodyRequest {
  email?: string;
}

export interface IRestoreAccountBodyRequest {
  email: string;
  restoreDate: string;
}

export interface IGetReferrerVideoResponse {
  id: number | null;
}

interface IPhotoOptionsProps {
  width: number;
  height: number;
  url: string;
}

interface IUserSharedResponse {
  tracks: number;
  followers: number;
  following: number;
}

export interface IProfileInviteResponse {
  welcomeMail: string;
  authorizationFlow: string;
  settings: string;
  hub: string;
  newsFeed: string;
}

export interface IPhotoResponse {
  thumbnail: IPhotoOptionsProps;
  small: IPhotoOptionsProps;
  normal: IPhotoOptionsProps;
  big: IPhotoOptionsProps;
}

export interface IProfileTariffPlanResponse {
  id: TariffPlanCodeEnum;
  price: string;
  startTime: string;
  endTime: string;
  method: PaymentMethodEnum;
  status: TariffPlanStatusEnum;
}

export interface IUserSettingsResponse {
  language: LanguageCodesEnum;
  hideLocation: boolean;
  notifyNewMentions: boolean;
  notifyNewMessages: boolean;
  notifyNewTracks: boolean;
  notifyNewVideos: boolean;
  questStatus: QuestStatusEnum;
  followStatus: FollowSuggestionStatusEnum;
  skillLevel: number | null;
}

export interface IProfileResponse {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  photo: IPhotoResponse;
  phone: string;
  location: string;
  description: string;
  invite: IProfileInviteResponse;
  tariffPlan: IProfileTariffPlanResponse;
  shared: IUserSharedResponse;
  settings: IUserSettingsResponse;
}

export type UpdateProfileBodyResponseType = XOR<
  ITokensResponse,
  ISuccessEmptyResponse
>;

export interface IUpdateProfileBodyRequest {
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  location?: string;
  description?: string;
  settings?: {
    language?: LanguageCodesEnum;
    hideLocation?: boolean;
    notifyNewTracks?: boolean;
    notifyNewVideos?: boolean;
    notifyNewMentions?: boolean;
    notifyNewMessages?: boolean;
  };
}

export interface IForgotPasswordRequest {
  email: string;
}

export interface ISetNewPasswordRequest {
  restore: string;
  password: string;
}

export interface IUpdatePasswordRequest {
  oldPassword: string;
  newPassword: string;
}

export interface IQuestTopicResponse {
  id: number;
  name: string;
}

export enum QuestStatusEnum {
  NeedToAsk = 0,
  Done = 1,
  Skipped = 2,
  NeverAsk = 3,
}

export interface IQuestSelectRequest {
  questStatus: QuestStatusEnum;
  topicIds?: number[];
}

export enum FollowSuggestionStatusEnum {
  NeedToAsk = 0,
  Done = 1,
  Skipped = 2,
  NeverAsk = 3,
}

export interface IUsersSuggestionStatusRequest {
  followStatus: FollowSuggestionStatusEnum;
}

// Hub

export interface IRelatedTutorialsParamsRequest {
  extraCategoryId?: TutorialExtraCategoryIdEnum;
}

export interface ITutorialBaseResponse {
  id: number;
  title: string;
  cover: string;
  covers: IPhotoResponse[];
  description: string;
  uploadDate: string;
  isForTrial?: boolean;
  courseId?: number;
}

export interface ITutorialUserStatsResponse {
  isLiked: boolean;
  isSaved: boolean;
  isWatched?: boolean;
}

export interface ITutorialResponse extends ITutorialBaseResponse {
  source: string;
  likes: number;
  views: number;
  userStats: ITutorialUserStatsResponse;
  comments: number;
}

export interface ITutorialsResponse extends IPaginationResponse {
  videos: ITutorialBaseResponse[];
}

export interface ITutorialSubtitleResponse {
  id: number;
  text: string;
  start: number;
  end: number;
}

export interface ITutorialSubtitlesResponse
  extends IPaginationResponse {
  subtitles: ITutorialSubtitleResponse[];
}

export interface ITutorialsRelatedCategoryResponse {
  name: string;
  description: string;
}

export interface ITutorialsRelatedResponse
  extends ITutorialsResponse {
  totalCount: number;
  category: ITutorialsRelatedCategoryResponse;
}

interface ISubCategoryResponse {
  id: number;
  name: string;
  description: string;
  videosCount: number;
}
export interface ICategoryResponse extends ISubCategoryResponse {
  children: ISubCategoryResponse[];
}

export interface ICategoriesResponse extends ICategoryResponse {
  children: ICategoryResponse[];
}

export interface ITutorialsBodyRequest {
  categoryIds: number[];
  tagIds: number[];
}

export interface ITutorialUpdateSaveStatusParams {
  status: TutorialSaveStatusEnum;
}

export enum TutorialSaveStatusEnum {
  Save = 'save',
  Unsave = 'unsave',
}

export interface ITutorialSaveWatchTimeParams {
  totalMilliseconds: number;
  id: number;
  sessionId?: number;
}

export interface ITagsRequestParams extends IPaginationRequestParams {
  recommendedTags?: boolean;
}

export interface ITagsResponse extends IPaginationResponse {
  tags: ITagResponse[];
}

export interface ITagResponse {
  id: number;
  name: string;
  order?: number;
  isRecommended?: boolean;
}

// Contact us

export interface IEnquiryTypeResponse {
  id: number;
  name: string;
}

export interface IContactUsMessageBodyRequest {
  enquiryTypeId: number;
  description: string;
}

export interface IContactUsTicketBodyRequest {
  title: string;
  message: string;
}

export interface ITutorialSubtitlesRequestParams {
  language: LanguageCodesEnum;
}

export interface ITutorialTimestampResponse {
  time: number;
  text: string;
}

// Tracks

export enum TrackFeedbackSubmitOptionEnum {
  Free = 0,
  Credit = 1,
}

export interface ISendTrackFeedbackParams {
  trackLink: string;
  referenceLink?: string;
  aspectsDescription?: string;
  feedbackOptionType?: TrackFeedbackSubmitOptionEnum | null;
}

export enum TrackFeedbackStatusEnum {
  New = 0,
  Reviewed = 1,
  UnableToReview = 2,
}

export interface ITrackFeedBack {
  id: number;
  sendingDate: string;
  status: TrackFeedbackStatusEnum;
  isFree: boolean;
  trackLink: string;
}

export interface ICredits {
  id: number;
  name: string;
  count: number;
  expirationDate: string;
  price: number | null;
}

export interface ILastFeedbackResponse {
  track: ITrackFeedBack;
  tracks: ITrackFeedBack[];
  isNewPeriod: boolean;
  credits: ICredits;
}

export interface ICreditPrice {
  id: number;
  name: string;
  price: number;
  count: number;
  termOfUse: number;
}

export interface ICreditPricesOffers {
  creditOffers: ICreditPrice[];
}

export interface ICreateCreditsPaymentIntentRequestParams {
  creditsOfferId: number;
}

// Users

export interface IUserPublicResponse {
  id: number;
  firstName: string;
  lastName: string;
  photo: object;
  location: string;
  description: string;
  isFollowing: boolean;
  shared?: IUserSharedResponse;
}

export interface IUsersResponse extends IPaginationResponse {
  totalCount: number;
  users: IUserPublicResponse[];
}

export enum FriendshipTypeEnum {
  Followers = 'followers',
  Following = 'following',
}

export interface IFriendshipUsersRequest
  extends IIdRequestParams,
    IPaginationRequestParams {
  friendshipType: FriendshipTypeEnum;
}

export enum UserFollowActionEnum {
  Follow = 'follow',
  Unfollow = 'unfollow',
}

export interface IToggleFollowRequest extends IIdRequestParams {
  action: UserFollowActionEnum;
}

// User videos watched

export interface IWatchProgressResponse {
  watchedCount: number;
  totalCount: number;
  showProgress: boolean;
}

// Notifications

export enum NotificationTokenActionEnum {
  Add = 'add',
  Delete = 'delete',
}

export interface IPushNotificationsTokenRequest {
  token: string;
}

export enum NotificationTypeEnum {
  NewMessage = 7, // Notification on the received new message in chat
  NewFeedLike = 6, // Notification on new like on News feed
  NewMention = 5, // Notification on the mentioned user in Video Tutorial comments
  NewTrack = 4, // New tracks of the people you are following
  NewVideo = 3, // Notification on the new video
  SubDay = 2, // Notifications on a day of subscription finish
  SubBefore = 1, // Notifications two days before subscription finish
}

export interface INotification {
  type: NotificationTypeEnum;
  url: string;
  objectId: number;
}

export interface INotificationResponse {
  id: number;
  type: NotificationTypeEnum;
  message: string;
  isNew: boolean;
  url: string;
  objectId?: number;
}

export type NotificationsResponseType = INotificationResponse[];

export interface ISetNotificationsAsReadRequestParams {
  newestId: number;
}

// Linking

export interface IShortLinkRequestParams {
  link: string;
}

export interface IShortLinkResponse {
  shortLink: string;
  previewLink: string;
}

// Payment

export interface ITariffPlanPriceResponse {
  regular: string;
  current: string;
}

export interface ITariffPlanSaveResponse {
  fromCode: number;
  toCode: number;
  value: string;
}

export interface ITariffPlanResponse {
  code: TariffPlanCodeEnum;
  upgradeCode: number | null;
  name: string;
  price: ITariffPlanPriceResponse;
  benefits: string[];
}

export interface ITariffPlansResponse {
  plans: ITariffPlanResponse[];
  save: ITariffPlanSaveResponse[];
  couponActivate: boolean;
}

export interface ITariffPlansRequestParams {
  referrer?: string;
}

export interface IPaymentMethodResponse {
  paymentMethodId: string;
  type: PaymentMethodTypeEnum;
  last4: string | null;
  brand: string | null;
  expMonth: string | null;
  expYear: string | null;
  email: string | null;
  payerId: string | null;
  country: string | null;
  wallet: PaymentMethodWalletEnum | null;
  isDefault: boolean;
}

export interface IPaymentMethodsResponse extends IPaginationResponse {
  items: IPaymentMethodResponse[];
}

export interface ISetupPaymentMethodResponse {
  setupIntentId: string;
  clientSecret: string;
}

export interface IConfirmPaymentMethodRequestParams {
  setupIntentId: string;
}

export interface IPaymentMethodIdResponse {
  paymentMethodId: string;
}

export interface IChangeTariffPlanRequestParams {
  tariffPlan: TariffPlanCodeEnum;
  couponId?: number;
  sessionId?: number;
}

export interface IConfirmChangeTariffPlanRequestParams {
  paymentIntentId: string;
  tariffPlan: TariffPlanCodeEnum;
}

interface ICreateSubscriptionPaymentIntentUserInfoRequestParams {
  email: string;
  countryCode: string;
  language?: LanguageCodesEnum;
}

export interface ICreateSubscriptionPaymentIntentRequestParams {
  tariffPlan: TariffPlanCodeEnum;
  userInformation: ICreateSubscriptionPaymentIntentUserInfoRequestParams;
  referrer?: string;
  couponId?: number;
  sessionId?: number;
}

export interface IPaymentClientSecretResponse {
  clientSecret: string;
  paymentIntentId: string;
}

export interface IConfirmPaymentIntentRequestParams {
  paymentIntentId: string;
}

export interface ILegacyPaymentClientSecretResponse {
  requiredAction: boolean;
  clientSecret: string;
}

export interface IValidateCouponCodeRequestParams {
  code: string;
}

export interface IValidateCouponCodeResponse {
  id: number;
  newAmount: string;
  discount: number;
  tariffPlan: TariffPlanCodeEnum;
}

// Messaging

export interface IConversationResponse {
  id: number;
  user: IUserPublicResponse;
  newCount: number;
  lastMessage: string | null;
}

export interface IConversationsResponse extends IPaginationResponse {
  conversations: IConversationResponse[];
}

export interface IBaseMessageResponse {
  id: number;
  userId: number;
  message: string;
  isIncome: boolean;
  sendTime: string;
}

export interface IRealtimeMessageResponse
  extends IBaseMessageResponse {
  conversationId: number;
}

export interface IMessagesResponse extends IPaginationResponse {
  messages: IBaseMessageResponse[];
}

export interface ISendMessageRequestParams {
  recipientId: number;
  message: string;
  sendTime: string;
}

export interface ISendMessageResponse {
  addedMessageId: number;
}

export interface INewMessagesCountResponse {
  conversationId: number;
  newCount: number;
  lastMessage: string | null;
}

// Feed

export interface IPostUserStatsResponse {
  isLiked: boolean;
}

export interface IPostDataResponse {
  captionLinkText?: string;
  captionLinkUrl?: string;
  actionText?: string;
  actionUrl?: string;
  text?: string;
  videoSourceCover?: string;
  videoSource?: string;
  images: IPhotoResponse[];
  likes: number;
  publishDate: string;
  userStats: IPostUserStatsResponse;
  comments: number;
  isForTrial?: boolean;
}

export interface IPostResponse {
  id: number;
  type: PostTypeEnum;
  userId?: number;
  itemData: IPostDataResponse;
}

export interface IPostsResponse extends IPaginationResponse {
  items: IPostResponse[];
}

export interface ICreatePostParams {
  text?: string;
  photoIds?: string[];
  sendTime: string;
  videoId?: string;
}

export interface IUpdatePostParams {
  text?: string;
  photoIds?: string[];
  videoId?: string;
}

export interface IPostPhotoResponse {
  id: string;
  photo: IPhotoResponse;
}

export interface IPostVideoResponse {
  id: string;
  video: string;
  videoCover?: string;
}

export interface IPostLikesResponse extends IPaginationResponse {
  users: IUserPublicResponse[];
}

// Reports

export interface IReportReasonTypeResponse {
  reasonTypeId: number;
  title: string;
}

export interface ISendReportBodyRequest {
  reasonTypeIds: number[];
}

// Comments

export interface ICommentUserStatsResponse {
  isLiked: boolean;
}

export interface ICommentResponse {
  id: number;
  userId: number;
  message: string;
  likes: number;
  sendTime: string;
  comments: number;
  userStats: ICommentUserStatsResponse;
}

export interface ICommentsResponse extends IPaginationResponse {
  items: ICommentResponse[];
}

export interface ICreateCommentParams {
  parentCommentId?: number;
  message: string;
}

export interface ICommentIdParams {
  commentId: number;
}

export interface ICommentsRequestParams
  extends IPaginationRequestParams {
  parentId?: number;
}

export interface IUpdateCommentParams {
  message: string;
}

export interface ILikesApi {
  getPostLikes(
    params: IIdRequestParams & IPaginationRequestParams,
  ): Promise<
    AxiosResponse<IPostLikesResponse, IPaginationRequestParams>
  >;
}

export interface ICommentApi {
  getComments(
    params: IIdRequestParams & ICommentsRequestParams,
  ): Promise<
    AxiosResponse<ICommentsResponse, ICommentsRequestParams>
  >;
  createComment(
    params: IIdRequestParams & ICreateCommentParams,
  ): Promise<AxiosResponse<IIdResponse, ICreateCommentParams>>;
  updateCommentLikeStatus(
    params: IIdRequestParams &
      IUpdateLikeStatusParams &
      ICommentIdParams,
    abortController: AbortController,
  ): Promise<AxiosResponse<ISuccessEmptyResponse>>;
  sendCommentReport(
    params: IIdRequestParams &
      ISendReportBodyRequest &
      ICommentIdParams,
  ): Promise<
    AxiosResponse<ISuccessEmptyResponse, ISendReportBodyRequest>
  >;
  updateComment(
    params: IIdRequestParams &
      IUpdateCommentParams &
      ICommentIdParams,
  ): Promise<
    AxiosResponse<ISuccessEmptyResponse, IUpdateCommentParams>
  >;
  deleteComment(
    params: IIdRequestParams & ICommentIdParams,
  ): Promise<AxiosResponse<ISuccessEmptyResponse>>;
}

// Search

type TutorialSearchMatchesPositionType = {
  title?: string;
  description?: string;
};
interface ITutorialBaseSearchResponse extends ITutorialBaseResponse {
  matchesPosition: TutorialSearchMatchesPositionType;
}

type UserSearchMatchesPositionType = {
  firstName?: string;
  lastName?: string;
};

interface IUserPublicSearchResponse extends IUserPublicResponse {
  matchesPosition: UserSearchMatchesPositionType;
}

export interface ITutorialsSearchResponse
  extends IPaginationResponse {
  videos: ITutorialBaseSearchResponse[];
}

export interface IUsersSearchResponse extends IPaginationResponse {
  users: IUserPublicSearchResponse[];
}

export interface ISearchCommonResponse {
  videos: ITutorialBaseSearchResponse[];
  users: IUserPublicSearchResponse[];
}

export interface ISetSkillLevelRequest {
  skillLevel: number;
}

interface ISkillLevelQuestAnswer {
  id: number;
  text: string;
  value: number;
}

export interface ISkillLevelQuestResponse {
  question: string;
  answers: ISkillLevelQuestAnswer[];
}

export interface ICourseDuration {
  videosCount: number;
  lessonsCount: number;
}

export interface ICourseIntroVideo {
  id: number;
  source: string;
  covers: IPhotoResponse[];
}

export interface IProgressStatus {
  isStarted: boolean;
  isCompleted: boolean;
}

export interface ICourseUserStats extends IProgressStatus {
  currentVideoId: number;
}

export interface ICourseBaseResponse {
  id: number;
  title: string;
  description: string;
  durationCourse: ICourseDuration;
  covers: IPhotoResponse[];
  benefits: string[];
  introVideo: ICourseIntroVideo;
  studentsCount: number;
  isForTrial: boolean;
  userStats?: ICourseUserStats;
  skillLevel: string;
}

export interface ICoursesResponse extends IPaginationResponse {
  courses: ICourseBaseResponse[];
}

export enum SectionTypeEnum {
  Latest = 0,
  Recommended = 1,
  Popular = 2,
  Tag = 3,
  Saved = 4,
  History = 5,
  Section = 6,
  Courses = 7,
}

export interface ISectionParamsResponse {
  isTag: boolean;
  videoTagId: null | number;
}

export interface ISectionResponse {
  name: string;
  type: SectionTypeEnum;
  params?: ISectionParamsResponse;
  items: (ITutorialBaseResponse | ICourseBaseResponse)[];
}

export interface ISectionsResponse extends IPaginationResponse {
  sections: ISectionResponse[];
}

export interface ICourseLessonResponse {
  id: number;
  name: string;
  description: string;
  courseId: number;
  order: number;
  videos: ITutorialBaseResponse[];
  userStats: IProgressStatus;
}

export interface ICourseLessonsResponse {
  totalVideosCount: number;
  watchedVideosCount: number;
  lessons: ICourseLessonResponse[];
}

export interface ICourseTutorialMarkAsDoneResponse {
  courseId: number;
  totalVideosCount: number;
  watchedVideosCount: number;
}

export interface ICourseTutorialMarkAsDoneRequestParams {
  courseId: number;
  tutorialId: number;
}

export interface ICourseSetCurrentTutorialRequestParams {
  courseId: number;
  tutorialId: number;
}

export interface ICourseLessonRequestParams extends IIdRequestParams {
  lessonId: number;
}

export interface IGenericItemsResponse extends IPaginationResponse {
  items: (ITutorialBaseResponse | ICourseBaseResponse)[];
}
